import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {humanFileSize} from '@src/helpers/humanFileSize';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import {AsyncFileUploaderContext} from '@src/components/AsyncFileUploader/AsyncFileUploaderContext';
import {Events} from '@src/@interfaces/services/Http/IAsyncUploader';
import {isEqual} from '@src/helpers/is-equal';
import {ISubscription} from '@src/@interfaces/services/Events/ISubscription';
import CircularProgressBar from '@src/components/ProgressBar/Circular/CircularProgressBar';
import classnames from '@src/helpers/classnames';
import usePermissionGranted from '@src/hooks/usePermissionGranted';

export default function FileListTableRow(props: {
    id?: string | number | null,
    title: string,
    size: number,
    isNewFile: boolean,
    index: number;
    onPreview?: (index: number) => Promise<void>,
    onOpen?: () => Promise<void>,
    onDelete: () => Promise<boolean>,
    uploaderContext?: Record<string, any>,
    disabled?: boolean,
}) {
    const {getItems, getItem, listen} = useContext(AsyncFileUploaderContext);
    const [searchParams] = useSearchParams();
    const [hash, setHash] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [progress, setProgress] = useState(0);
    const DeleteButtonComponent = (props.isNewFile || !props.onOpen) ? 'button' : ConfirmableButton;
    const isAllowToDelete = usePermissionGranted('orders@can-delete-order-product-files') && !props.disabled;

    useEffect(() => {
        const listener = () => {
            const item = getItems().find((item) => {
                return (
                        item.getFile()?.name === props.title &&
                        item.getFile()?.size === props.size &&
                        isEqual(item.getMeta().context, props.uploaderContext)
                );
            });

            const newHash = item?.getMeta().uniqueId ?? null;
            if (newHash !== hash) {
                setHash(newHash);
            }
        };

        const subscriptions: ISubscription[] = [];
        subscriptions.push(listen([Events.FILE_ADDED_TO_LIST, Events.FILE_REMOVED_FROM_LIST], listener));

        subscriptions.push(listen<{ uniqueId: string }>([
            Events.SEND,
            Events.PAUSE,
            Events.ABORT,
            Events.COMPLETE,
            Events.PROGRESS,
        ], (event, {uniqueId}) => {
            if (!hash || hash !== uniqueId) {
                return;
            }

            const item = getItem(hash);
            if (!item) {
                return;
            }

            const {isLoading, error} = item.getMeta();
            setIsLoading(isLoading);
            setError(error);
            setProgress(isLoading ? item.getProgress() : 0);
        }));

        listener();

        return () => subscriptions.forEach(item => item.unsubscribe());
    }, [props, getItems, listen, hash, getItem]);

    const {index, onPreview} = props;
    const onTitleClicked = useCallback(() => {
        if (onPreview) {
            return onPreview(index);
        }

        return Promise.resolve();
    }, [index, onPreview]);

    return <tr className={classnames('order__files-list__row', {
        'order__files-list__row--highlighted': (
                props.id != null &&
                searchParams.get('fileId') === String(props.id)
        )
    })}>
        <td className="order__files-list__row__title-cell">
            <div className={classnames('order__files-list__row__title', {
                'order__files-list__row__title--clickable': index >= 0 && onPreview != null,
            })} onClick={onTitleClicked}>{props.title}</div>
            {error ? <div className="order__files-list__row__error-message">{error?.message}</div> : null}
        </td>
        <td>
            <div>
                <span>{humanFileSize(props.size ?? 0)}</span>

                {(props.isNewFile || !props.onOpen) ? (
                        <CircularProgressBar
                                color="info"
                                size={24}
                                progress={progress}
                                trackWidth={2}
                                className="order__files-list__row__uploading-progress"
                                containerSizesModifiers={[1, 1]}
                                transparentTrack
                        >
                            {
                                error ?
                                        <i className="fas fa-triangle-exclamation text-danger" title={error.message}/> :
                                        <i className="far fa-clock"/>
                            }
                        </CircularProgressBar>
                ) : (
                        <button
                                type="button"
                                className="order__files-list__row__download-button"
                                onClick={props.onOpen}
                        >
                            <i className="fas fa-download"/>
                        </button>
                )}

                {(!props.isNewFile && !isAllowToDelete) ? null : <DeleteButtonComponent
                        type="button"
                        className="order__files-list__row__remove-button"
                        disabled={isDeleting || props.disabled}
                        style={isDeleting ? {'--bs-btn-padding-x': '0.31rem'} as React.CSSProperties : {}}
                        onClick={() => {
                            if (props.isNewFile || !props.onOpen) {
                                props.onDelete().finally(() => {
                                    setTimeout(() => {
                                        if (isLoading && hash) {
                                            getItem(hash)?.abort();
                                        }
                                    }, 10);
                                });

                                return;
                            }

                            if (!isAllowToDelete) {
                                return;
                            }

                            setIsDeleting(true);
                            props.onDelete().finally(() => {
                                setIsDeleting(false);
                            });
                        }}
                >
                    {
                        isDeleting ?
                                <LoadingIndicator size="sm" type="border" style={{marginBottom: '-3px'}}/> :
                                <i className="fa fa-trash"/>
                    }

                </DeleteButtonComponent>}
            </div>
        </td>
    </tr>;
}
